// useDatabaseOpenIntent.js

import { useCallback } from 'react';

import { useNavigate } from 'react-router-dom';

import { useCollection } from 'context/DatabaseContext';
import { useNavigation } from 'context/NavigationContext';
import { useNodePeek } from 'context/NodePeekContext';
import { useUI } from 'context/UIContext';
import { useUser } from 'context/UserContext';

import { getCollection } from 'services/collections';

import { SectionType } from 'utilities/Enums';
import { buildCollectionPath } from 'utilities/collectionPaths';

function cleanString(value) {
  return String(value || '').trim();
}

function resolveDatabaseKey(databaseOrKey) {
  if (typeof databaseOrKey === 'string') {
    return cleanString(databaseOrKey);
  }

  return cleanString(databaseOrKey?.key);
}

function buildDatabaseStub({
  databaseKey,
  typeKey = '',
  baseKey,
  title = '',
  userKey = null,
} = {}) {
  return {
    key: databaseKey,
    baseKey: cleanString(baseKey),
    type: cleanString(typeKey),
    title: cleanString(title),
    userKey: userKey || null,
    filters: [],
  };
}

const useDatabaseOpenIntent = () => {
  const navigate = useNavigate();
  const { setActiveCollection: setActiveDatabase } = useCollection();
  const { setCurrentNode, setCurrentSection } = useNavigation();
  const { setSelectedLocation } = useUI();
  const { closePeek } = useNodePeek();
  const { user } = useUser();

  const openDatabase = useCallback(async (databaseOrKey, options = {}) => {
    const databaseKey = resolveDatabaseKey(databaseOrKey);
    if (!databaseKey) return;

    let database = (
      databaseOrKey
      && typeof databaseOrKey === 'object'
      && cleanString(databaseOrKey.key)
    )
      ? databaseOrKey
      : null;

    if (!database && options.fetch !== false) {
      try {
        database = await getCollection(databaseKey);
      } catch (error) {
        console.error('[useDatabaseOpenIntent] Failed to load collection', error);
      }
    }

    if (!database) {
      database = buildDatabaseStub({
        databaseKey,
        typeKey: options.typeKey,
        baseKey: options.baseKey,
        title: options.title,
        userKey: user?.key || null,
      });
    }

    closePeek();
    setActiveDatabase(database);
    setCurrentNode(null);
    setSelectedLocation(null);
    setCurrentSection(SectionType.NODE);
    navigate(buildCollectionPath(databaseKey));
  }, [
    closePeek,
    navigate,
    setActiveDatabase,
    setCurrentNode,
    setCurrentSection,
    setSelectedLocation,
    user?.key,
  ]);

  return {
    openDatabase,
    openCollection: openDatabase,
  };
};

export default useDatabaseOpenIntent;
